import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState, useCallback, useMemo } from "react";
import { LeadsHeader } from "shared/app/LeadsTableView/components/LeadsHeader";
import { LeadsTable } from "shared/app/LeadsTableView/slices/LeadsTable";
import { NoLeadsSlice } from "shared/app/LeadsTableView/slices/NoLeadsSlice";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";
import { fetchStages, stagesQueryKey } from "views/settings-crm/api";
import { StageKind } from "views/settings-crm/types/stage";
import queryString from "qs";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { Show } from "shared/components";
import { FilterBadge } from "shared/app/LeadsTableView/components/FilterBadge";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export interface LeadsFilters {
  properties?: { id: string; name: string }[];
  stages?: { id: string; name: string }[];
  leadSources?: { id: string; name: string }[];
  activityTypes?: string[];
}

export const LeadsTableView = ({
  hasHeaderTitle = false,
  property_ids,
  unit_ids,
  contact_id,
  viewPortHeightOffset = 120
}: {
  hasHeaderTitle?: boolean;
  property_ids?: string[];
  unit_ids?: string[];
  contact_id?: number;
  viewPortHeightOffset?: number;
}) => {
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      page: "1",
      per_page: DEFAULT_PER_PAGE
    },
    ["search", "page", "per_page"]
  );

  // State for assignee filtering
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<
    number[] | null
  >(null);

  const [debouncedSearch] = useDebouncedValue(searchParams.search, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<LeadsFilters>();

  const propertyIds = useMemo(
    () => filters?.properties?.map((p) => p.id),
    [filters?.properties]
  );
  const stageIds = useMemo(
    () => filters?.stages?.map((s) => s.id),
    [filters?.stages]
  );
  const leadSourceIds = useMemo(
    () => filters?.leadSources?.map((ls) => ls.id),
    [filters?.leadSources]
  );

  const removeFilter = useCallback(
    <K extends keyof LeadsFilters>(key: K, id: string) => {
      setFilters((prev) => {
        const arr = prev?.[key];
        if (!arr?.length) return prev;
        return {
          ...prev,
          [key]:
            key === "activityTypes"
              ? (arr as string[]).filter((item) => item !== id)
              : (arr as { id: string; name: string }[]).filter(
                  (item) => item.id !== id
                )
        };
      });
      setSearchParams({ ...searchParams, page: "1" });
    },
    [searchParams, setSearchParams]
  );

  const {
    data: queryLeadsData,
    refetch: refetchLeads,
    isFetching
  } = useQuery({
    queryKey: [
      "LEADS_TABLE",
      searchParams.page,
      debouncedSearch,
      selectedAssigneeIds,
      propertyIds,
      stageIds,
      leadSourceIds,
      contact_id,
      filters?.activityTypes
    ],
    queryFn: async () => {
      return await instance
        .get(
          "/portal/v1/leads?" +
            queryString.stringify(
              {
                page: searchParams.page,
                per_page: 10,
                property_ids: property_ids || propertyIds,
                unit_ids: unit_ids,
                stage_ids: stageIds?.length ? stageIds : undefined,
                source_ids: leadSourceIds?.length ? leadSourceIds : undefined,
                partner_user_ids: selectedAssigneeIds?.length
                  ? selectedAssigneeIds
                  : undefined,
                most_recent_activity: filters?.activityTypes?.length
                  ? { type: filters.activityTypes }
                  : undefined,
                search: debouncedSearch.trim() || undefined,
                contact_id
              },
              {
                arrayFormat: "brackets"
              }
            )
        )
        .then((res) => res.data as PaginatedResponse<Lead>);
    }
  });

  const { data: queryStagesData } = useQuery({
    queryKey: stagesQueryKey(StageKind.Lead),
    queryFn: () => fetchStages(StageKind.Lead)
  });

  const hasActiveFilters = Object.values(filters ?? {}).some((f) => f?.length);
  const hasAssigneeFilter = Boolean(selectedAssigneeIds?.length);
  const hasSearch = Boolean(debouncedSearch.trim());

  if (
    queryLeadsData?.items.length === 0 &&
    !hasSearch &&
    !isFetching &&
    !hasActiveFilters &&
    !hasAssigneeFilter
  )
    return (
      <NoLeadsSlice
        hasHeaderTitle={hasHeaderTitle}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
      />
    );

  return (
    <>
      <LeadsHeader
        search={searchParams.search}
        hasHeaderTitle={hasHeaderTitle}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        setSearch={(search) =>
          setSearchParams({ ...searchParams, page: "1", search: search })
        }
        selectedAssigneeIds={selectedAssigneeIds}
        setSelectedAssigneeIds={setSelectedAssigneeIds}
        onPageChange={(page) => setSearchParams({ ...searchParams, page })}
        setShowFilters={setShowFilters}
      />
      <Show if={hasActiveFilters}>
        <div className="flex flex-wrap gap-2 mb-4">
          {filters?.properties?.map((property) => (
            <FilterBadge
              key={property.id}
              filter={property.name}
              onRemove={() => removeFilter("properties", property.id)}
            />
          ))}
          {filters?.stages?.map((stage) => (
            <FilterBadge
              key={stage.id}
              filter={stage.name}
              onRemove={() => removeFilter("stages", stage.id)}
            />
          ))}
          {filters?.leadSources?.map((leadSource) => (
            <FilterBadge
              key={leadSource.id}
              filter={leadSource.name}
              onRemove={() => removeFilter("leadSources", leadSource.id)}
            />
          ))}
          {filters?.activityTypes?.map((activityType) => (
            <FilterBadge
              key={activityType}
              filter={activityType}
              onRemove={() => removeFilter("activityTypes", activityType)}
            />
          ))}
        </div>
      </Show>
      <LeadsTable
        stagesData={queryStagesData || []}
        isLoading={isFetching}
        data={queryLeadsData?.items || []}
        perPageRows={Number(DEFAULT_PER_PAGE)}
        viewPortHeightOffset={viewPortHeightOffset}
        pagination={
          <div className="flex items-center justify-between">
            <p className="font-medium text-content-1000 text-base">
              Total leads: {queryLeadsData?.total_count || 0}
            </p>
            <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
              <Pagination
                className="md:justify-end justify-center"
                total={queryLeadsData?.page_count || 0}
                current={Number(searchParams.page)}
                onChange={(p) =>
                  setSearchParams({ ...searchParams, page: String(p) })
                }
              />
            </div>
          </div>
        }
      />
      <FiltersDrawer
        property_ids={property_ids}
        unit_ids={unit_ids}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onApplyFilters={() => setSearchParams({ ...searchParams, page: "1" })}
        search={debouncedSearch}
      />
    </>
  );
};
